<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => !v && close()"
    header="Задача"
    :modal="true"
    :style="{ width: '620px' }"
    :breakpoints="{ '700px': '95vw' }"
    class="task-detail-dialog"
  >
    <div v-if="task" class="detail-body">
      <!-- Заголовок + срочность -->
      <div class="detail-head">
        <h2 class="detail-title" :class="{ 'done-text': task.is_done }">{{ task.title }}</h2>
        <Tag :value="urgency.label" :severity="urgency.severity" />
      </div>

      <!-- Статус -->
      <div class="detail-status">
        <Tag
          :value="task.is_done ? 'Выполнена' : 'В работе'"
          :severity="task.is_done ? 'success' : 'info'"
          :icon="task.is_done ? 'pi pi-check' : 'pi pi-clock'"
        />
      </div>

      <!-- Свойства -->
      <div class="detail-props">
        <div class="prop">
          <span class="prop-label">Исполнитель</span>
          <span v-if="task.assignee" class="prop-assignee">
            <span class="assignee-avatar" :style="{ background: avatarBg }">{{ initials }}</span>
            {{ task.assignee.username }}
            <span class="role-muted">({{ roleLabel(task.assignee.role) }})</span>
          </span>
          <span v-else class="prop-muted">Не назначен</span>
        </div>
        <div class="prop">
          <span class="prop-label">Срок</span>
          <span v-if="dueText" :class="{ overdue: overdue && !task.is_done }">{{ dueText }}</span>
          <span v-else class="prop-muted">Не задан</span>
        </div>
        <div class="prop">
          <span class="prop-label">Метка</span>
          <Tag v-if="task.label" :value="task.label" severity="contrast" icon="pi pi-tag" />
          <span v-else class="prop-muted">—</span>
        </div>
        <div class="prop">
          <span class="prop-label">Создана</span>
          <span>{{ createdText }}</span>
        </div>
      </div>

      <!-- Описание -->
      <div class="detail-section">
        <span class="section-label">Описание</span>
        <p v-if="task.description" class="detail-description">{{ task.description }}</p>
        <p v-else class="prop-muted">Без описания</p>
      </div>

      <!-- Фотографии -->
      <div v-if="task.images && task.images.length" class="detail-section">
        <span class="section-label">Фотографии ({{ task.images.length }})</span>
        <div class="detail-images">
          <Image
            v-for="img in task.images"
            :key="img.id"
            :src="img.url"
            :alt="img.original_name || ''"
            preview
            imageClass="detail-thumb"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="detail-footer">
        <Button
          :label="task && task.is_done ? 'Вернуть в работу' : 'Отметить выполненной'"
          :icon="task && task.is_done ? 'pi pi-undo' : 'pi pi-check'"
          :severity="task && task.is_done ? 'secondary' : 'success'"
          @click="$emit('toggle', task)"
        />
        <span class="footer-spacer"></span>
        <Button label="Редактировать" icon="pi pi-pencil" severity="secondary" variant="text" @click="$emit('edit', task)" />
        <Button label="Удалить" icon="pi pi-trash" severity="danger" variant="text" @click="$emit('delete', task)" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Image from 'primevue/image'
import {
  urgencyMeta, formatDueDate, isOverdue,
  userInitials, avatarColor, roleLabel
} from '../../utils/taskConstants'

const props = defineProps({
  visible: { type: Boolean, default: false },
  task: { type: Object, default: null }
})
const emit = defineEmits(['update:visible', 'toggle', 'edit', 'delete'])

const urgency = computed(() => urgencyMeta(props.task?.urgency))
const dueText = computed(() => formatDueDate(props.task?.due_date))
const overdue = computed(() => isOverdue(props.task?.due_date))
const createdText = computed(() => formatDueDate(props.task?.created_at) || '—')
const initials = computed(() => userInitials(props.task?.assignee?.username))
const avatarBg = computed(() => avatarColor(props.task?.assignee?.username))

function close() {
  emit('update:visible', false)
}
</script>

<style scoped>
.detail-body {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.detail-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-color);
  word-break: break-word;
}

.done-text {
  text-decoration: line-through;
  color: var(--text-color-secondary);
}

.detail-status {
  margin-top: -0.5rem;
}

.detail-props {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem 1.2rem;
  padding: 0.9rem 0;
  border-top: 1px solid var(--surface-200);
  border-bottom: 1px solid var(--surface-200);
}

.prop {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.prop-label,
.section-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-color-secondary);
}

.prop-assignee {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.92rem;
}

.role-muted {
  color: var(--text-color-secondary);
  font-size: 0.82rem;
}

.assignee-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.prop-muted {
  color: var(--text-color-secondary);
}

.overdue {
  color: var(--red-500);
  font-weight: 600;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-description {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-color);
  line-height: 1.5;
}

.detail-images {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.detail-images :deep(.detail-thumb) {
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--surface-200);
  cursor: pointer;
}

.detail-footer {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
}

.footer-spacer {
  flex: 1;
}

.dark-mode .detail-props {
  border-color: var(--surface-700);
}
</style>
